import React, { useState, useEffect } from "react";
import axios from "axios";
import Layouts from "../../layouts/layouts";
import DataTable_Component from "../../components/clientinout/Data Table Component/index";
import ClientinoutDetailModal from "../../components/clientinout/Clientinout Details";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* 📍 Fetch-on-click component for live location */
const LiveLocationCell = ({ coordinates, id }) => {
  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAddress = async () => {
    if (!coordinates?.latitude || !coordinates?.longitude) {
      setAddress("No coordinates available");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${coordinates.latitude},${coordinates.longitude}&key=${process.env.REACT_APP_BASE_API}`,
      );
      const data = await res.json();

      if (data.status === "OK" && data.results.length > 0) {
        setAddress(data.results[0].formatted_address);
      } else {
        setAddress("Address not found");
      }
    } catch (err) {
      console.error("Error fetching address:", err);
      setAddress("Error fetching address");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "relative", zIndex: 1 }}>
      {address ? (
        <>
          <span>{address.slice(0, 20)}...</span>
          <FontAwesomeIcon
            icon={faInfoCircle}
            data-tooltip-id={`tooltip-live-location-${id}`}
            data-tooltip-content={address}
            style={{ cursor: "pointer", marginLeft: "5px" }}
          />
          <Tooltip
            id={`tooltip-live-location-${id}`}
            place="bottom"
            style={{ zIndex: 9999, backgroundColor: "black" }}
          />
        </>
      ) : (
        <button
          onClick={fetchAddress}
          disabled={loading}
          style={{
            padding: "4px 8px",
            background: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "12px",
          }}
        >
          {loading ? "Loading..." : "Click for Live-Location"}
        </button>
      )}
    </div>
  );
};

export default function ClientInOut() {
  const [apistate, setApiState] = useState([]);
  const [apicol, setApiCol] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalRows, setTotalRows] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [clientFilter, setClientFilter] = useState("");
  const [selectedClientinout, setSelectedClientinout] = useState(null);
  const [numberplateFilter, setNumberplateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statusCounts, setStatusCounts] = useState({
    EARLY: 0,
    ON_TIME: 0,
    LATE: 0,
    TOTAL: 0,
  });
  useEffect(() => {
    console.log("STATUS FILTER:", statusFilter);
  }, [statusFilter]);
  const navigate = useNavigate();

  const [showClientinoutDetailModal, setShowClientinoutDetailModal] =
    useState(false);

  /* 📦 Define table columns */
  const columns = [
    {
      name: <b>Vehicle Number</b>,
      selector: (row) => row.numberplate || "N/A",
      sortable: true,
    },
    {
      name: <b>Arrived Client Location</b>,
      cell: (row) => renderTimings(row, "arrived client_location") || "N/A",
      sortable: false,
    },
    {
      name: <b>Left Client Location</b>,
      cell: (row) => renderTimings(row, "left client_location") || "N/A",
      sortable: false,
    },
    {
      name: <b>Total Kilometers</b>,
      selector: (row) => row.totalClientKm || "-",
      sortable: true,
    },
    {
      name: <b>Working Hours</b>,
      selector: (row) => row.workingHours || "-",
      sortable: true,
    },
    {
      name: <b>Status</b>,
      cell: (row) => {
        const status = row.shiftAnalysis?.status;

        const statusStyle = {
          EARLY: {
            backgroundColor: "#FEF3C7",
            color: "#B45309",
            border: "1px solid #FCD34D",
          },
          ON_TIME: {
            backgroundColor: "#DCFCE7",
            color: "#166534",
            border: "1px solid #86EFAC",
          },
          LATE: {
            backgroundColor: "#FEE2E2",
            color: "#B91C1C",
            border: "1px solid #FCA5A5",
          },
          TOTAL: {
            backgroundColor: "#FEF3C7",
            color: "#B45309",
            border: "1px solid #FCD34D",
          },
        };

        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "90px",
              padding: "6px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: "600",
              letterSpacing: "0.4px",
              ...statusStyle[status],
            }}
          >
            {status === "EARLY" && "⏰ "}
            {status === "ON_TIME" && "✓ "}
            {status === "LATE" && "⚠ "}
            {status === "TOTAL" && "✓ "}
            {status || "N/A"}
          </span>
        );
      },
      sortable: true,
    },

    // {
    //   name: <b>Reached Count</b>,
    //   selector: (row) => row.reachedCount || "-",
    //   sortable: true,
    // },

    {
      name: <b>Live Location</b>,
      cell: (row) => (
        <button
          className="btn btn-success btn-sm"
          onClick={() => {
            const vehicleId = row.vehicleid || row._id; // fallback to _id if vehicleid missing
            if (!vehicleId) {
              console.error("❌ Missing vehicle ID in row:", row);
              return;
            }
            navigate(`/tracking/${vehicleId}`, {
              state: { date: new Date().toISOString().slice(0, 10) },
            });
          }}
        >
          View Live
        </button>
      ),
      sortable: false,
    },
    {
      name: <b>Client Name</b>,
      cell: (row) => {
        if (!Array.isArray(row.vehicleTimings)) return "-";
        const names = row.vehicleTimings.map((v) => v.clientName);
        const uniqueNames = [...new Set(names)].join(", ");
        return uniqueNames || "-";
      },
      sortable: true,
    },
    {
      name: <b>Action</b>,
      cell: (row) => (
        <button
          onClick={() => viewClientinout(row)}
          className="btn btn-primary btn-sm ms-1"
        >
          <FontAwesomeIcon icon={faEye} />
        </button>
      ),
    },
  ];

  const viewClientinout = (clientinout) => {
    setSelectedClientinout(clientinout);
    setShowClientinoutDetailModal(true);
  };

  /* 🕒 Render timings (each event block with click-to-fetch location) */
  const renderTimings = (row, type) => {
    if (!Array.isArray(row.vehicleTimings)) return "N/A";

    const filtered = row.vehicleTimings.filter((t) => t.type === type);
    if (filtered.length === 0) return "-";

    return (
      <div style={{ maxWidth: "300px", whiteSpace: "pre-wrap" }}>
        {filtered.map((timing, index) => {
          const driverName =
            timing.currentDriver?.name ||
            row.vehicleDetail?.currentDriverName ||
            "N/A";

          return (
            <div key={index} style={{ marginBottom: "8px" }}>
              🕒{" "}
              <b>
                {new Date(timing.time).toLocaleString("en-IN", {
                  timeZone: "Asia/Kolkata",
                })}
              </b>
              <br />
              👤 <b>Driver:</b> {driverName}
              <br />
              📍{" "}
              <LiveLocationCell
                coordinates={timing.liveCoordinates}
                id={row.numberplate + "-" + index}
              />
            </div>
          );
        })}
      </div>
    );
  };

  /* 🔁 Fetch API data (role-based authorization) */
  const fetchData = async (
    selectedDate = date,
    page = currentPage,
    limit = rowsPerPage,
    selectedClient = clientFilter,
    numberplate = numberplateFilter,
    status = statusFilter,
  ) => {
    try {
      const token = localStorage.getItem("token");
      const userRole = localStorage.getItem("userRole"); // 👈 assuming stored at login
      if (!token || !userRole) {
        console.error("Missing token or userRole");
        return;
      }

      const date = selectedDate || new Date().toISOString().split("T")[0];

      let endpoint = "";
      if (userRole === "superadmin") {
        endpoint = `${process.env.REACT_APP_BASE_URL}/api/v4/vehicle-tracking/vehicle-locations?date=${selectedDate}&page=${page}&limit=${limit}&client=${encodeURIComponent(selectedClient || "")}&numberplate=${encodeURIComponent(numberplate || "")}&status=${encodeURIComponent(status || "")}`;
      } else if (userRole === "admin") {
        endpoint = `${process.env.REACT_APP_BASE_URL}/api/v4/vehicle-tracking/admin/vehicle-locations?date=${selectedDate}&page=${page}&limit=${limit}&client=${encodeURIComponent(selectedClient || "")}&numberplate=${encodeURIComponent(numberplate || "")}&status=${encodeURIComponent(status || "")}`;
      } else {
        console.error("Unauthorized role:", userRole);
        return;
      }
      console.log("STATUS GOING TO API:", status);
      const response = await axios.get(endpoint, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("✅ API Response Sample:", response.data[0]);

      if (Array.isArray(response.data.vehicles)) {
        setApiState(response.data.vehicles);

        setTotalRows(response.data.pagination?.totalRecords || 0);
        setStatusCounts({
          EARLY: response.data.shiftSummary?.early || 0,
          ON_TIME: response.data.shiftSummary?.onTime || 0,
          LATE: response.data.shiftSummary?.late || 0,
          TOTAL: response.data.shiftSummary?.total || 0,
        });
        console.log("Vehicles Length:", response.data.vehicles.length);
        console.log(
          "Pagination Total:",
          response.data.pagination?.totalRecords,
        );
      } else {
        console.error("Unexpected data format from API", response.data);
        setApiState([]);
        setTotalRows(0);
      }

      setApiCol(columns);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 404) {
        setApiState([]);
        setTotalRows(0);
      }
    }
  };

  useEffect(() => {
    fetchData(
      date,
      currentPage,
      rowsPerPage,
      clientFilter,
      numberplateFilter,
      statusFilter,
    );
  }, [
    date,
    currentPage,
    rowsPerPage,
    clientFilter,
    numberplateFilter,
    statusFilter,
  ]);

  const exportToExcel = async () => {
    try {
      const token = localStorage.getItem("token");
      const userRole = localStorage.getItem("userRole");

      if (!token || !userRole) {
        alert("Missing authentication");
        return;
      }

      let endpoint = "";

      // Use a very large limit to fetch all records
      const limit = 100000;

      if (userRole === "superadmin") {
        endpoint = `${process.env.REACT_APP_BASE_URL}/api/v4/vehicle-tracking/vehicle-locations?date=${date}&page=1&limit=${limit}&client=${encodeURIComponent(
          clientFilter || "",
        )}&numberplate=${encodeURIComponent(numberplateFilter || "")}
&status=${encodeURIComponent(statusFilter || "")}`;
      } else if (userRole === "admin") {
        endpoint = `${process.env.REACT_APP_BASE_URL}/api/v4/vehicle-tracking/admin/vehicle-locations?date=${date}&page=1&limit=${limit}&client=${encodeURIComponent(
          clientFilter || "",
        )}&numberplate=${encodeURIComponent(numberplateFilter || "")}
&status=${encodeURIComponent(statusFilter || "")}`;
      } else {
        alert("Unauthorized");
        return;
      }

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const vehicles = response.data?.vehicles || [];

      if (!vehicles.length) {
        alert("No data available");
        return;
      }

      const excelData = vehicles.map((vehicle) => ({
        "Vehicle Number": vehicle.numberplate || "-",
        "Client Name": vehicle.clientName || "-",

        "In Time": vehicle.actualArrival
          ? new Date(vehicle.actualArrival).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
            })
          : "-",

        "Out Time": vehicle.actualDeparture
          ? new Date(vehicle.actualDeparture).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
            })
          : "-",

        "Working Hours": vehicle.workingHours || 0,
        "Total Client KM": vehicle.totalClientKm || 0,

        Status: vehicle.shiftAnalysis?.status || "-",
        "Delay Minutes": vehicle.shiftAnalysis?.delayMinutes || 0,
        "Early Minutes": vehicle.shiftAnalysis?.earlyMinutes || 0,
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Client InOut Report");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const data = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });

      saveAs(data, `Client_InOut_${date}.xlsx`);
    } catch (error) {
      console.error("Export Error:", error);
      alert("Failed to export data");
    }
  };

  return (
    <>
      <Layouts />
      <ClientinoutDetailModal
        show={showClientinoutDetailModal}
        onHide={() => setShowClientinoutDetailModal(false)}
        clientinout={selectedClientinout}
      />
      <div className="container-fluid">
        <div className="row my-3">
          <div className="col-12">
            <div className="card" style={{ borderTop: "2px solid #4723d9" }}>
              <div className="card-header d-flex justify-content-between border-bottom pb-1">
                <h4>Vehicle In/Out From Client</h4>
                <div>
                  {" "}
                  <button
                    className="btn btn-success btn-sm me-2"
                    onClick={exportToExcel}
                  >
                    Export Excel
                  </button>
                </div>
              </div>
              <div className="card-body">
                <DataTable_Component
                  searchBy="numberplate"
                  title_btn={false}
                  apidata={apistate}
                  columns={apicol}
                  applyFiltersExternally={fetchData}
                  totalRows={totalRows}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  clientFilter={clientFilter}
                  setClientFilter={setClientFilter}
                  dataFilter={numberplateFilter}
                  setDataFilter={setNumberplateFilter}
                  statusCounts={statusCounts}
                  statusFilter={statusFilter}
                  setStatusFilter={setStatusFilter}
                  date={date}
                  setDate={setDate}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
